'use client';

import { useEffect, useState } from 'react';
import { differenceInDays, isValid, parseISO } from 'date-fns';

const STORAGE_KEY = 'promoBannerLastTime';

/**
 * Open state of the promo popup, shared by both of its designs.
 *
 * Opens once the visitor has scrolled past 1.2 viewports, at most once per
 * day per browser. The day is stamped when the popup opens, not on mount:
 * visitors who never scroll that far keep that day's popup, and React's dev
 * StrictMode remount does not stamp the day away before the listener is
 * attached (see commit 48da3da).
 *
 * `forceOpen` is for preview harnesses: opens immediately and stamps nothing.
 */
export function usePromoPopupOnce(forceOpen = false): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      return;
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    const lastDate = parseISO(stored || '');

    if (
      stored &&
      isValid(lastDate) &&
      differenceInDays(new Date(), lastDate) < 1
    ) {
      return;
    }

    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 1.2) {
        localStorage.setItem(
          STORAGE_KEY,
          new Date().toISOString().split('T')[0],
        );
        setOpen(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [forceOpen]);

  return [open, setOpen];
}
